import type { AlertType } from '../types';

const LABELS: Record<AlertType, string> = {
  stalled_start: 'Stalled start',
  near_completion: 'Near completion',
  stale_activity: 'Stale activity',
  stale_activity_30d: 'No session 30d',
};

// Reuse the existing .pill color scheme — failed=red, running=amber, active=blue
// so the stalled (red) / near-completion (amber) / stale (amber) palette
// matches without adding new CSS.
const CLASSNAMES: Record<AlertType, string> = {
  stalled_start: 'failed',
  near_completion: 'running',
  stale_activity: 'cancelled',
  stale_activity_30d: 'running',
};

export function AlertTypePill({ type }: { type: AlertType }): JSX.Element {
  return (
    <span className={`pill ${CLASSNAMES[type]}`}>
      <span className="pdot" />
      {LABELS[type]}
    </span>
  );
}

// Compact variant for the risk-ranking Signals column. Most rows carry two to
// four signals, so the full .pill treatment (and even a small-caps label) reads
// as a cramped 3-line stack. These are calm one-word tokens — colored dot,
// sentence-case label, tabular points — that wrap 2-3 per line; the full signal
// name lives in the title tooltip.
const SIG_CLASSNAMES: Record<AlertType, string> = {
  stalled_start: 'stalled',
  near_completion: 'near',
  stale_activity: 'stale',
  stale_activity_30d: 'nosession',
};

// Short forms keyed to the KPI sub-labels: "SOW ending, still open" → Ending,
// "started, no session in 30+ days" → Idle 30d.
const SIG_SHORT: Record<AlertType, string> = {
  stalled_start: 'Stalled',
  near_completion: 'Near done',
  stale_activity: 'Ending',
  stale_activity_30d: 'Idle 30d',
};

export function SignalChip({ type, points }: { type: AlertType; points: number }): JSX.Element {
  return (
    <span className={`sig ${SIG_CLASSNAMES[type]}`} title={`${LABELS[type]} · +${points} pts`}>
      <span className="sdot" />
      <span className="slabel">{SIG_SHORT[type]}</span>
      <span className="d-tnum pts">+{points}</span>
    </span>
  );
}
